import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Calendar, Download, BarChart3, 
  DollarSign, ShoppingBag, Package, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPOSReports() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [activeTab, setActiveTab] = useState('sales');
  
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    groupBy: 'day'
  });

  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSalesReport();
    } else {
      fetchProductReport();
    }
  }, [filters, activeTab]);

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        group_by: filters.groupBy
      });

      const response = await fetch(`${API_URL}/api/admin/pos/reports/sales?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSalesData(data.data || []);
    } catch (error) {
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: filters.dateFrom,
        date_to: filters.dateTo
      });

      const response = await fetch(`${API_URL}/api/admin/pos/reports/products?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProductData(data || []);
    } catch (error) {
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totalSales = salesData.reduce((sum, d) => sum + (d.total_sales || 0), 0);
  const totalOrders = salesData.reduce((sum, d) => sum + (d.order_count || 0), 0);
  const totalCash = salesData.reduce((sum, d) => sum + (d.cash_sales || 0), 0);
  const totalUPI = salesData.reduce((sum, d) => sum + (d.upi_sales || 0), 0);
  const totalCard = salesData.reduce((sum, d) => sum + (d.card_sales || 0), 0);

  const maxSales = Math.max(...salesData.map(d => d.total_sales || 0), 1);

  return (
    <div className="space-y-6" data-testid="admin-pos-reports">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">POS Reports</h1>
          <p className="text-gray-500">Sales analytics and insights</p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Export Report
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border shadow-sm p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              className="w-40"
            />
          </div>
          {activeTab === 'sales' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
              <select
                value={filters.groupBy}
                onChange={(e) => setFilters({...filters, groupBy: e.target.value})}
                className="h-10 px-3 rounded-lg border bg-white min-w-[120px]"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'sales' ? 'default' : 'outline'}
          onClick={() => setActiveTab('sales')}
          className={activeTab === 'sales' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Sales Report
        </Button>
        <Button
          variant={activeTab === 'products' ? 'default' : 'outline'}
          onClick={() => setActiveTab('products')}
          className={activeTab === 'products' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <Package className="w-4 h-4 mr-2" />
          Product Report
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : activeTab === 'sales' ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-sm text-gray-500">Total Sales</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">₹{totalSales.toLocaleString('en-IN')}</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">Total Orders</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                </div>
                <span className="text-sm text-gray-500">Avg Order</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ₹{totalOrders > 0 ? Math.round(totalSales / totalOrders).toLocaleString('en-IN') : 0}
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm text-gray-500">Period</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{salesData.length} {filters.groupBy}s</p>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Payment Method Breakdown</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-emerald-700">Cash</p>
                <p className="text-xl font-bold text-emerald-700">₹{totalCash.toLocaleString('en-IN')}</p>
                <p className="text-xs text-emerald-600">
                  {totalSales > 0 ? ((totalCash / totalSales) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="p-4 bg-violet-50 rounded-xl">
                <p className="text-sm text-violet-700">UPI</p>
                <p className="text-xl font-bold text-violet-700">₹{totalUPI.toLocaleString('en-IN')}</p>
                <p className="text-xs text-violet-600">
                  {totalSales > 0 ? ((totalUPI / totalSales) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-700">Card</p>
                <p className="text-xl font-bold text-blue-700">₹{totalCard.toLocaleString('en-IN')}</p>
                <p className="text-xs text-blue-600">
                  {totalSales > 0 ? ((totalCard / totalSales) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>

          {/* Sales Chart */}
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Sales Trend</h3>
            <div className="space-y-3">
              {salesData.map((day, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="text-sm text-gray-500 w-24">{day._id}</span>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-lg flex items-center justify-end pr-2"
                      style={{ width: `${(day.total_sales / maxSales) * 100}%` }}
                    >
                      {day.total_sales > maxSales * 0.1 && (
                        <span className="text-xs text-white font-medium">
                          ₹{day.total_sales.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium w-20 text-right">
                    {day.order_count} orders
                  </span>
                </div>
              ))}
              
              {salesData.length === 0 && (
                <p className="text-center text-gray-500 py-8">No sales data for this period</p>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Product Report */
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="font-semibold text-gray-900">Top Selling Products</h3>
            <p className="text-sm text-gray-500">Products ranked by revenue</p>
          </div>
          
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-sm text-gray-500">
                <th className="px-6 py-4 font-medium">#</th>
                <th className="px-6 py-4 font-medium">Product</th>
                <th className="px-6 py-4 font-medium text-right">Quantity Sold</th>
                <th className="px-6 py-4 font-medium text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {productData.map((product, idx) => (
                <tr key={product._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-500">{idx + 1}</td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{product.product_name}</p>
                  </td>
                  <td className="px-6 py-4 text-right font-medium">{product.total_quantity}</td>
                  <td className="px-6 py-4 text-right">
                    <span className="font-semibold text-emerald-600">
                      ₹{product.total_revenue?.toLocaleString('en-IN')}
                    </span>
                  </td>
                </tr>
              ))}
              
              {productData.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No product data for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
